import { useMemo, useState } from 'react';
import { styled } from 'styled-components';

import { Box } from '#/components/Box/Box';
import { RadioButtons } from '#/components/Radio/RadioButtons';
import { H2 } from '#/components/Text/H2';
import { HeadingSmall } from '#/components/Text/HeadingSmall';
import { useAppConfig } from '#/config/AppConfigContext';
import type { AppConfig } from '#/config/types/AppConfig';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { CmsApiPersonaQuestion } from '#/modules/cms/api/types/blocks/CmsApiPersonaQuizBlock';
import { colors } from '#/theme/theme';
import { imageUrl } from '#/utils/filestack';
import { retinaBackgroundImage } from '#/utils/style';

const TitleImage = styled(Box)<{
  $handle: string;
  $mimetype: string | undefined;
  $appConfig: AppConfig;
}>`
  background-size: cover;
  background-position: center;
  ${({ $handle: handle, $mimetype: mimetype, $appConfig: appConfig }) =>
    retinaBackgroundImage(
      imageUrl({
        data: { handle, mimetype },
        width: 900,
        height: 500,
        fit: 'clip',
        insecureSVG: false,
        appConfig,
      }),
      {
        filename2x: imageUrl({
          data: { handle, mimetype },
          width: 900,
          height: 500,
          fit: 'clip',
          insecureSVG: false,
          appConfig,
        }),
        include3x: false,
      },
    )};
`;

type Props = {
  blockIndex?: number;
  question: CmsApiPersonaQuestion;
  index: number;
  updateScore?: (index: number, points: number[]) => void;
  answered?: boolean;
};

export function CmsPersonaQuizBlockQuestion({
  blockIndex,
  question: { title, image, answers },
  index,
  updateScore,
  answered,
}: Props) {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();

  const options = useMemo(
    () =>
      answers.map(({ title: label }, value) => ({
        label,
        value,
      })),
    [answers],
  );
  const [value, setValue] = useState(null);

  // oxlint-disable-next-line typescript/no-explicit-any
  const onChange = (newValue: any) => {
    const newIndex = parseInt(newValue, 10);
    // @ts-expect-error TS(2345): Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
    setValue(newIndex);

    if (updateScore) {
      updateScore(index, answers[newIndex].points);
    }
  };

  return (
    <Box
      qaId={`cms-persona-quiz-question-${index}`}
      // @ts-expect-error TS(2322): Type 'false | (number | null)[]' is not assignable... Remove this comment to see the full error message
      mt={index > 0 && [32, null, 72]}
      opacity={answered ? 0.7 : 1}
    >
      <HeadingSmall mb={18}>
        <Box
          display="inline-block"
          bg={colors.inputGrey}
          color={colors.lightContentGrey}
          borderRadius="3px"
          px="8px"
        >
          {`${i18n('Question')} ${index + 1}`}
        </Box>
      </HeadingSmall>
      <Box
        position="relative"
        width={1}
        overflow="hidden"
        borderRadius={16}
        textAlign="center"
      >
        {image && (
          <TitleImage
            qaId="question-image"
            $handle={image.handle}
            $mimetype={image.mimetype}
            position="absolute"
            top={0}
            left={0}
            width={1}
            height="100%"
            $appConfig={appConfig}
          />
        )}
        <Box
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={1}
          minHeight={['56.25vw', null, null, 500]}
          p={[24, null, null, 64]}
          bg="rgba(0,0,0,0.4)"
          color="white"
        >
          <H2
            analyticsData={
              blockIndex
                ? {
                    eventName: 'CMS Block',
                    eventProperties: {
                      block_position: blockIndex,
                      block_type: 'Persona Quiz',
                    },
                  }
                : undefined
            }
            qaId="question-title"
            styleAs={(title ?? '').length > 80 ? 'h2' : 'h1'}
            my={0}
            dangerouslySetInnerHTML={{
              // @ts-expect-error TODO fix me
              __html: title,
            }}
          />
        </Box>
      </Box>
      <Box mt={36} textAlign="left">
        <RadioButtons
          large
          hideRadioButtons
          buttons={options}
          value={value}
          qaId="question-answers"
          onChange={onChange}
          columns={2}
          disabled={answered}
        />
      </Box>
    </Box>
  );
}
